import { useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router';
import {
  LogOut,
  ArrowLeftRight,
  ChevronDown,
  Sun,
  Moon,
  Monitor,
  Loader2,
  Copy,
  Check,
  Gift,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client/react';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { useThemeStore } from '@/stores/theme-store';
import { usePermissions } from '@/hooks/usePermissions';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { setScioVersionCookie, isBehindCfWorker } from '@/lib/sso-cookies';
import { useReferralAccess } from '@/features/referral/hooks/useReferralAccess';
import { analyticsReferralMenuClicked } from '@/lib/analytics-service';
import { SCIO_SWITCH_LOGS, ME_QUERY, type MeQueryResult } from '@scio3/graphql-client';
import { fullLogout } from '@/lib/logout';
import { useCanManageTags } from '@/features/tags/hooks/useCanManageTags';
import { ViewToggle } from '@/components/shared/ViewToggle';
import { showLoadingOverlay, dismissLoadingOverlay } from '@/components/shared';
import { useTeamDisplayName } from '@/hooks/useRootTeamspaceLabel';
import { TOP_NAV_TEAM_SWITCHER } from '@/features/group-security/constants';
import { TeamSwitcher } from './TeamSwitcher';
import { useGroupSecurityCohort } from '@/hooks/useGroupSecurityCohort';
import { useTopNavOwnsTeamScope } from './useTopNavTeamScope';
import { useCanSwitchTeamScope } from './TeamScopeList';
import { useUserManagementAccess } from './useUserManagementAccess';
import { LanguageSwitcher } from './LanguageSwitcher';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@scio3/ui';

function getInitials(firstName?: string, lastName?: string, fallbackName?: string): string {
  const first = firstName?.trim()[0];
  const last = lastName?.trim()[0];
  if (first || last) return ((first ?? '') + (last ?? '')).toUpperCase();
  if (fallbackName) {
    const parts = fallbackName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return '?';
}

const THEME_OPTIONS = [
  { value: 'light', icon: Sun, label: 'Light' },
  { value: 'dark', icon: Moon, label: 'Dark' },
  { value: 'system', icon: Monitor, label: 'System' },
] as const;

interface UserMenuProps {
  compact?: boolean;
}

export function UserMenu({ compact }: UserMenuProps) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const activeTeam = useTeamStore((s) => s.getActiveTeam());
  const navigate = useNavigate();
  const client = useApolloClient();
  const { isRootAdmin, isRootSuperAdmin, isTeamAdmin, can, canAny } = usePermissions();
  // Group Security v5 §4e — this menu is the PARALLEL render path for the SettingsLayout sidebar
  // (see .claude/rules/frontend/white-label-branding.md: gating one path and not its twin is a bug),
  // so every key below is the same one that opens the matching sidebar entry.
  //
  // Each is `can(key) || <the role gate that was here before>` — additive, so no user loses an item
  // they see today. Several groups had to be SPLIT: one `isRootAdmin &&` used to wrap items from
  // five different domains, which a single permission cannot express.
  // Config/editor pages take `.manage`, read surfaces take `.view`.
  // #2452 — the cohort is read HERE (moved up from below) because the capability arm of
  // `mayCompanySettings` depends on it.
  const cohort = useGroupSecurityCohort();
  // #2452 scan finding 3 — the capability arm is ON-COHORT ONLY. `company.view` sits in
  // MEMBER_BASELINE_PERMISSIONS, i.e. every member of every role bundle holds it, so on a legacy
  // account `can('company.view')` is true for everyone and Company Settings appeared for the whole
  // company. origin/main gates this row on `isRootAdmin` alone. Writes still require
  // `company.manage`, so the leak is page REACHABILITY, not escalation — but it is a page a
  // standard user has never been able to open, and it opens by URL too.
  const mayCompanySettings = (cohort && can('company.view')) || isRootAdmin;
  const mayCompanyManage = can('company.manage') || isRootAdmin;
  const mayPreferences = can('company.manage') || isRootSuperAdmin;
  const userManagementAccess = useUserManagementAccess();
  const canSwitchTeamScope = useCanSwitchTeamScope();
  // #2452 scan finding 22 — THE TWIN. This suppresses the user-management entries below on the
  // grounds that `TeamScopeButton`'s Manage footer carries them instead. But that footer only
  // exists when the TOP BAR owns the teamspace chooser, which is `TOP_NAV_TEAM_SWITCHER && cohort`
  // — and this condition was missing the cohort term. On a legacy account the entries were
  // suppressed here while the footer that should replace them never rendered, so they vanished
  // with nothing in their place. That is the same shape as the original team-switcher regression,
  // which is exactly why `useTopNavOwnsTeamScope` exists: ONE predicate, so the two cannot drift.
  const topNavOwnsTeamScope = useTopNavOwnsTeamScope();
  const isUserManagementInTeamMenu = topNavOwnsTeamScope && canSwitchTeamScope;
  const mayBillingView = can('billing.view') || isRootAdmin;
  const mayBillingManage = can('billing.manage') || isRootAdmin;
  const mayOrdersView = can('orders.view') || isRootAdmin;
  const mayOrdersCreate = can('orders.create') || isRootAdmin;
  const mayProofOfPlayManage = can('proofOfPlay.manage') || isRootAdmin;
  const mayMonitoringManage = can('monitoring.manage') || isRootAdmin;
  const mayAudit = can('audit.view') || isRootAdmin;
  const mayBranding = can('branding.view') || isRootAdmin;
  const maySso = can('sso.view') || isRootAdmin;
  const mayApiKeys = can('apiKeys.view') || isRootAdmin;
  // external-com's open-api gates became `devices.manage` in the v5 conversion.
  const mayExternalCom = can('devices.manage') || isTeamAdmin;
  const { canAdministerTags } = useCanManageTags();
  const features = useCompanyFeatures();
  // QC #1272 — child / linked sub-accounts can't use Sub-Account Billing
  // (backend rejects activation). Hide the dropdown entry to avoid dead-ends.
  const isChildAccount = !!company?.parentAccountId || !!company?.linkedAccountId;

  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);

  // Persistent entry point into the referral program. The floating launcher
  // disappears after 3 dismissals, so this menu item keeps "Refer & Earn"
  // findable — it navigates to the full `/app/s/referral` page (in the settings
  // area, with the account sidebar). Gated on paid subscription via useReferralAccess.
  const { entryEnabled: referralEnabled } = useReferralAccess();

  const isWhiteLabel = !!me?.brandingData?.isWhiteLabel;
  // #2452 — the legacy cohort's user-management entry; same branding gate as main.
  const hideAccountMembersPage = !!me?.brandingData?.hideAccountMembersPage;
  // Lapsed account. Those pages are hard-gated in
  // SettingsLayout, so leaving their menu entries here would just bounce the user back to
  // the paywall with a permission toast. The first four entries below (Personal Profile,
  // Company Settings, Account Members, Subscription Plan + Payment Settings) are exactly
  // the allowed set, so only the extras and the More submenu need gating. Logout at the
  // bottom deliberately stays — it is the only way out for a paywalled user.
  const isAccountInactive = useAuthStore((s) => s.isAccountInactive);
  // OptiSigns-branded surfaces (Integrations, Share Links, DataSources) show only when OptiSigns
  // branding is present — hidden once removed. Mirrors the SettingsLayout sidebar gate so the two
  // render paths stay in sync (scio1 parity).
  const removeOptiSignsBranding = !!me?.brandingData?.removeOptiSignsBranding;
  // White-label page-hide flags (scio1 account.html parity).
  const hideSubscriptionPages = !!me?.brandingData?.hideSubscriptionPages;
  const hidePaymentHistoryPages = !!me?.brandingData?.hidePaymentHistoryPages;
  const hideProofofPlay = !!me?.brandingData?.hideProofofPlay;
  const hideAuditLogging = !!me?.brandingData?.hideAuditLogging;
  const hideMonitorAndAlert = !!me?.brandingData?.hideMonitorAndAlert;
  const hideExternalCommunications = !!me?.brandingData?.hideExternalCommunications;

  const isDefaultTeam = useTeamStore((s) => s.isDefaultTeam);
  const displayName = useTeamDisplayName();
  // T982 / N-Q2 — with the team chooser promoted to the top bar, the chip stops doubling as a team
  // label: avatar only, no team name, no chevron, at every width. `compact` (the level-2 responsive
  // rung) still drives it on the flag-off path, where today's chip is preserved exactly.
  const avatarOnly = TOP_NAV_TEAM_SWITCHER || !!compact;
  const [switchingToClassic, setSwitchingToClassic] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  // Controlled so the referral promo card (a custom button, not a DropdownMenuItem)
  // can close the menu before navigating — matching how the item rows behave.
  const [menuOpen, setMenuOpen] = useState(false);
  const nameRef = useRef<HTMLParagraphElement>(null);
  const [nameTooltipOpen, setNameTooltipOpen] = useState(false);
  const handleNameHover = (open: boolean) => {
    if (!open) {
      setNameTooltipOpen(false);
      return;
    }
    const el = nameRef.current;
    setNameTooltipOpen(!!el && el.scrollWidth > el.clientWidth);
  };

  async function handleSwitchToClassic() {
    if (switchingToClassic) return;
    setSwitchingToClassic(true);
    showLoadingOverlay();

    const teamId = useTeamStore.getState().activeTeamId;
    // 1. Mutation sets user.scioVersion=1 in MongoDB — abort if it fails,
    //    otherwise scio1 reads stale scioVersion=3 → cookie loop.
    try {
      await client.mutate({ mutation: SCIO_SWITCH_LOGS, variables: { teamId } });
    } catch {
      console.error('Failed to switch to classic — mutation failed');
      setSwitchingToClassic(false);
      return;
    }

    // 2. Verify the write is readable — re-query ME until scioVersion≠3.
    //    This prevents scio1's initSCIO20() from reading stale scioVersion=3
    //    (MongoDB replication lag) and overwriting cookie back to 3 → infinite loop.
    for (let i = 0; i < 5; i++) {
      const { data } = await client
        .query<MeQueryResult>({
          query: ME_QUERY,
          fetchPolicy: 'network-only',
        })
        .catch(() => ({ data: null }));
      if (!data?.me?.scioVersion || data.me.scioVersion !== 3) break;
      await new Promise((r) => setTimeout(r, 300));
    }

    setScioVersionCookie('1');
    // Redirect to scio1 with current path + search params preserved.
    const url = new URL(window.location.href);
    if (!isBehindCfWorker()) {
      const scio1 = new URL(import.meta.env.VITE_SCIO1_URL);
      url.href = new URL(url.pathname + url.search, scio1.origin).href;
    }
    // Skip activeTeamId for designManagement — scio1's designManagement.sendAuth()
    // triggers a team-switch reload when $stateParams.teamId differs from
    // the current Meteor team, which causes window.location.reload() that
    // loses all query params (e.g. asset_id). The team is already synced
    // via the scioSwitchLogs mutation above.
    // Use activeTeamId (not teamId) to avoid collision with teamId which is
    // folder-level context for cross-team shared folders (#459).
    const isDesigner = window.location.pathname.endsWith('/designManagement');
    if (!isDesigner) {
      url.searchParams.set('activeTeamId', teamId || '1');
    }
    // scio1's engage page needs tilde-encoded slashes (/ → ~2F) in the path param.
    // Angular UI Router uses ~2F encoding; standard %2F doesn't work for engage (#348).
    const pathParam = url.searchParams.get('path');
    if (pathParam && url.pathname.endsWith('/engageManagement')) {
      url.searchParams.set('path', pathParam.replace(/\//g, '~2F'));
    }
    window.location.href = url.toString();

    // If the user cancels the browser's beforeunload dialog, the page stays alive.
    // After 500ms, if we're still here, dismiss the loading overlay.
    setTimeout(() => {
      setSwitchingToClassic(false);
      dismissLoadingOverlay();
    }, 500);
  }

  async function handleLogout() {
    await fullLogout(client);
    navigate('/signIn');
  }

  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={avatarOnly ? 'icon-sm' : 'sm'}
          className={cn(
            'rounded-full text-muted-foreground hover:text-foreground hover:bg-sidebar-active',
            avatarOnly ? 'h-10 w-10' : 'h-auto gap-1.5 py-1 pl-1 pr-3',
          )}
          aria-label={t('User menu')}
          data-testid="user-menu"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
            {getInitials(me?.firstName, me?.lastName, me?.name)}
          </span>
          {!avatarOnly && (
            <>
              <span className="min-w-0 max-w-[120px] truncate text-sm font-medium">
                {displayName(activeTeam?.name) || t('Account')}
              </span>
              <ChevronDown className="h-3 w-3" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {/* User info header */}
        <div className="px-3 py-2">
          <TooltipProvider>
            <Tooltip open={nameTooltipOpen} onOpenChange={handleNameHover}>
              <TooltipTrigger asChild>
                <p ref={nameRef} className="truncate text-sm font-semibold text-foreground">
                  {me?.name}
                </p>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start" className="max-w-xs break-all">
                {me?.name}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {me?.email && (
            <div
              className="group/copy relative cursor-pointer select-none"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(me.email ?? '');
                setEmailCopied(true);
                setTimeout(() => setEmailCopied(false), 1500);
              }}
            >
              <p className="text-xs text-muted-foreground truncate group-hover/copy:text-foreground transition-colors">
                {me.email}
              </p>
              <span className="absolute right-0 top-0 bottom-0 items-center hidden group-hover/copy:flex bg-gradient-to-l from-popover from-50% to-transparent pl-5">
                {emailCopied ? (
                  <Check className="h-3 w-3 text-success" />
                ) : (
                  <Copy className="h-3 w-3 text-muted-foreground" />
                )}
              </span>
            </div>
          )}
          {/* Inline theme toggle */}
          <div className="mt-2" onClick={(e) => e.stopPropagation()}>
            <ViewToggle
              value={theme}
              onValueChange={(v) => setTheme(v as 'light' | 'dark' | 'system')}
              options={THEME_OPTIONS.map(({ value, icon, label }) => ({
                value,
                icon,
                label: t(label),
              }))}
            />
          </div>
        </div>
        {/* Refer & Earn promo card (replaces the plain menu item below) — a
            tinted, single-tap card sitting directly under the mode selector.
            The whole card is the click target → opens the full Refer & Earn
            page, the same nav the old item used. Gated on the same referral
            flag. The gift tile reuses the shared `.ref-gift-chip` gradient so
            it matches the sidebar chip + widget hero. */}
        {referralEnabled && !isAccountInactive && (
          <div className="px-2 pb-1 pt-0.5">
            <button
              type="button"
              onClick={() => {
                analyticsReferralMenuClicked();
                setMenuOpen(false);
                navigate('/app/s/referral');
              }}
              data-testid="user-menu-referral"
              className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl border border-primary/20 bg-secondary px-3 py-2.5 text-left transition-colors hover:border-primary/40 hover:bg-primary/15"
            >
              <span className="ref-gift-chip flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-white shadow-[0_8px_16px_-8px_rgba(0,208,110,0.6)]">
                <Gift className="h-[18px] w-[18px]" strokeWidth={2} />
              </span>
              <span className="whitespace-nowrap text-sm font-semibold text-foreground">
                {t('Refer & Earn')}
              </span>
              <span className="ml-auto shrink-0 whitespace-nowrap rounded-full border border-primary/25 bg-card px-2.5 py-0.5 text-[11px] font-bold text-secondary-foreground">
                Get $25
              </span>
            </button>
          </div>
        )}
        {/* N-Q3 — the chooser MOVES to the top bar (`TeamScopeButton`), it does not live in two
            places: two entry points to one scope invite desync. Flag off ⇒ it comes back here.
            #2452 — "flag off" now also means a LEGACY account, whose top bar has no chooser at
            all (it does not exist on origin/main). Same predicate as TopNavigation, inverted, so
            the two can never both withhold it and leave the account with no switcher. */}
        {!topNavOwnsTeamScope && <TeamSwitcher />}
        {/* Main items */}
        <DropdownMenuItem onClick={() => navigate('/app/s/personal-profile')}>
          {t('Personal Profile')}
        </DropdownMenuItem>
        {mayCompanySettings && !isWhiteLabel && (
          <DropdownMenuItem onClick={() => navigate('/app/s/company-profile')}>
            {t('Company Settings')}
          </DropdownMenuItem>
        )}
        {/* #2452 — Account Members, restored for the LEGACY cohort. v3 removed this entry because
            the Users page superseded it; that is true only for accounts on the Group Security
            model. For everyone else it is their user management, and SettingsLayout already shows
            it again — leaving it out here meant the two menus disagreed about whether the page
            exists. Same scio1 branding gate main uses. */}
        {!cohort && !hideAccountMembersPage && (
          <DropdownMenuItem onClick={() => navigate('/app/s/account-members')}>
            {t('Account Members')}
          </DropdownMenuItem>
        )}
        {/* User Management (Group Security v3, D2) — mirrors the SettingsLayout "User Management" group.
            Account Members is superseded by the Users page and no longer listed here (page kept reachable
            by direct URL). Gates live in `useUserManagementAccess`. When the top-bar teamspace menu is showing, these MOVE
            there (its "Manage" section, same gates — `TeamScopeButton`); they only stay here for
            single-teamspace users, who get no teamspace menu. */}
        {!isUserManagementInTeamMenu && (
          <>
            {userManagementAccess.users && (
              <DropdownMenuItem onClick={() => navigate('/app/s/users')}>
                {t('Users')}
              </DropdownMenuItem>
            )}
            {userManagementAccess.teamspaces && (
              <DropdownMenuItem onClick={() => navigate('/app/s/teamspaces')}>
                {t('Teamspaces')}
              </DropdownMenuItem>
            )}
            {userManagementAccess.roles && (
              <DropdownMenuItem onClick={() => navigate('/app/s/roles')}>
                {t('Roles')}
              </DropdownMenuItem>
            )}
            {userManagementAccess.groups && (
              <DropdownMenuItem onClick={() => navigate('/app/s/group-security')}>
                {t('Groups')}
              </DropdownMenuItem>
            )}
          </>
        )}
        {!hideSubscriptionPages && (
          <>
            {mayBillingView && (
              <DropdownMenuItem onClick={() => navigate('/app/s/subscription-plan')}>
                {t('Subscription Plan')}
              </DropdownMenuItem>
            )}
            {/* A WRITE surface — `billing.manage`, not `billing.view`. */}
            {mayBillingManage && (
              <DropdownMenuItem onClick={() => navigate('/app/s/payment')}>
                {t('Payment Settings')}
              </DropdownMenuItem>
            )}
          </>
        )}
        {mayOrdersCreate && isDefaultTeam && !isWhiteLabel && !isAccountInactive && (
          <DropdownMenuItem onClick={() => navigate('/app/s/order-device')}>
            {t('Order Devices')}
          </DropdownMenuItem>
        )}
        {mayPreferences && !isWhiteLabel && !isAccountInactive && (
          <DropdownMenuItem onClick={() => navigate('/app/s/preference-settings')}>
            {t('Preferences')}
          </DropdownMenuItem>
        )}

        {/* More submenu - show for team admins and above */}
        {(isRootAdmin ||
          isTeamAdmin ||
          // A permission-only user reaches these items too, so the WRAPPER has to open for them —
          // gating the children alone would hide a submenu whose contents they are entitled to.
          canAny([
            'company.manage',
            'billing.view',
            'billing.manage',
            'orders.view',
            'proofOfPlay.manage',
            'monitoring.manage',
            'audit.view',
            'branding.view',
            'sso.view',
            'apiKeys.view',
            'devices.manage',
          ])) &&
          !isAccountInactive && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>{t('More')}</DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-56">
                  {/* Formerly ONE `isRootAdmin &&` wrapping five different domains — split so each
                    item answers to its own key (§4e). The white-label / hide-flag clauses are
                    unchanged. */}
                  {
                    <>
                      {/* OptiSigns-only — hidden for white-label (scio1 parity) */}
                      {mayCompanyManage && !isWhiteLabel && (
                        <DropdownMenuItem onClick={() => navigate('/app/s/operational-schedule')}>
                          {t('Operational Schedule')}
                        </DropdownMenuItem>
                      )}
                      {mayBillingView && !hidePaymentHistoryPages && (
                        <DropdownMenuItem onClick={() => navigate('/app/s/payment-history')}>
                          {t('Billing History')}
                        </DropdownMenuItem>
                      )}
                      {mayBillingManage && !isChildAccount && !isWhiteLabel && (
                        <DropdownMenuItem onClick={() => navigate('/app/s/sub-account-billing')}>
                          {t('Sub-Account Billing')}
                        </DropdownMenuItem>
                      )}
                      {/* <DropdownMenuItem onClick={() => navigate('/app/s/coupons')}>
                      {t('Coupons')}
                    </DropdownMenuItem> */}
                      {mayOrdersView && !isWhiteLabel && (
                        <DropdownMenuItem onClick={() => navigate('/app/s/ordered-device')}>
                          {t('Order History')}
                        </DropdownMenuItem>
                      )}
                      {/* The proof-of-play SETTINGS editor — `.manage`, not `.view` (the report
                        itself lives under the Analytics nav dropdown). */}
                      {mayProofOfPlayManage && !hideProofofPlay && (
                        <DropdownMenuItem onClick={() => navigate('/app/s/proof-of-play-settings')}>
                          {t('Proof of Play')}
                        </DropdownMenuItem>
                      )}
                    </>
                  }

                  {/* Branding settings — OptiSigns-only, hidden for white-label (scio1 parity) */}
                  {mayBranding && !isWhiteLabel && (
                    <DropdownMenuItem onClick={() => navigate('/app/s/branding-settings')}>
                      {t('Branding')}
                    </DropdownMenuItem>
                  )}

                  {/* Advanced admin items - ROOT admin only. Audit/Monitoring/External-Com are
                    shown on white-label ONLY when the matching add-on feature is enabled (scio1 parity). */}
                  {
                    <>
                      {mayAudit &&
                        !hideAuditLogging &&
                        (!isWhiteLabel || company?.feature?.securityAuditLogging) && (
                          <DropdownMenuItem onClick={() => navigate('/app/security-audit-logging')}>
                            {t('Audit Log')}
                          </DropdownMenuItem>
                        )}
                      {/* The alert-rules EDITOR — `monitoring.manage`, not `monitoring.view`. */}
                      {mayMonitoringManage &&
                        !hideMonitorAndAlert &&
                        (!isWhiteLabel || company?.feature?.alertPolicy) && (
                          <DropdownMenuItem
                            onClick={() => navigate('/app/s/monitoring-alert-rules')}
                          >
                            {t('Monitoring Alert')}
                          </DropdownMenuItem>
                        )}
                      {/* SAML & API Keys hidden for white-label (matches scio1) */}
                      {!isWhiteLabel && (
                        <>
                          {maySso && (
                            <DropdownMenuItem onClick={() => navigate('/app/s/saml-settings')}>
                              {t('SAML Single Sign On')}
                            </DropdownMenuItem>
                          )}
                          {mayApiKeys && (
                            <DropdownMenuItem onClick={() => navigate('/app/s/apikeys')}>
                              {t('API Keys')}
                            </DropdownMenuItem>
                          )}
                        </>
                      )}
                      {mayExternalCom &&
                        !hideExternalCommunications &&
                        (!isWhiteLabel || company?.feature?.externalCom) && (
                          <DropdownMenuItem onClick={() => navigate('/app/s/external-coms')}>
                            {t('External Communications')}
                          </DropdownMenuItem>
                        )}
                    </>
                  }

                  {/* Team-level items visible to team admins+ (matches scio1 isTeamAdmin).
                    OptiSigns-branded — hidden once OptiSigns branding is removed (SettingsLayout parity). */}
                  {!removeOptiSignsBranding && (
                    <DropdownMenuItem onClick={() => navigate('/app/s/integrations')}>
                      {t('Integrations')}
                    </DropdownMenuItem>
                  )}
                  {company?.feature?.shareLink && !removeOptiSignsBranding && (
                    <DropdownMenuItem onClick={() => navigate('/app/s/share-links-admin')}>
                      {t('Share Links')}
                    </DropdownMenuItem>
                  )}
                  {!removeOptiSignsBranding && (
                    <DropdownMenuItem onClick={() => navigate('/app/s/datasources-admin')}>
                      {t('DataSources')}
                    </DropdownMenuItem>
                  )}
                  {features.smallappManifest && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => navigate('/app/dev/smallapp-playground')}
                    >
                      {t('SmallApp Playground')}
                    </DropdownMenuItem>
                  )}
                  {isRootAdmin && features.contentApproval && (
                    <DropdownMenuItem onClick={() => navigate('/app/s/revue')}>
                      {t('Revue Workflow')}
                    </DropdownMenuItem>
                  )}
                  {/* gate mirrors SettingsLayout's Tags entry (backend assertCanAdministerTags — team admin+) */}
                  {canAdministerTags && (
                    <DropdownMenuItem onClick={() => navigate('/app/s/tags')}>
                      {t('Tags')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </>
          )}

        {/* Language */}
        <DropdownMenuSeparator />
        <LanguageSwitcher />

        {/* Switch to Classic & Logout */}
        <DropdownMenuSeparator />
        {/* scio1 parity: the UI-version switch (scio1's "Try New Interface") is hidden on
            white-label sites (topNavigation.html `!isWhiteLabel`) — hide "Switch to Classic" too. */}
        {!isWhiteLabel &&
          (isBehindCfWorker()
            ? !(company?.scioVersion != null && company.scioVersion > 1)
            : !!import.meta.env.VITE_SCIO1_URL) && (
            <DropdownMenuItem onClick={handleSwitchToClassic} disabled={switchingToClassic}>
              {switchingToClassic ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowLeftRight className="mr-2 h-4 w-4" />
              )}
              {switchingToClassic ? t('Switching…') : t('Switch to Classic')}
            </DropdownMenuItem>
          )}
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          {t('Log Out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
